import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { EditButton, DeleteButton } from '../../components/ActionButtons';
import { apiRequest } from '../../services/api';
import UsageInfo from '../../components/UsageInfo';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';

const DURUM_RENKLER = {
  'Temiz': { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-100', dot: 'bg-emerald-500' },
  'Dolu': { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100', dot: 'bg-blue-500' },
  'Kirli': { bg: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-100', dot: 'bg-rose-500' },
  'Arızalı': { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-100', dot: 'bg-amber-500' },
};
const DURUMLAR = ['Temiz', 'Dolu', 'Kirli', 'Arızalı'];

function FormHata({ mesaj }) {
  if (!mesaj) return null;
  return <p className="text-[10px] font-black text-rose-500 uppercase tracking-widest mt-2 flex items-center gap-1 animate-shake">⚠️ {mesaj}</p>;
}

// ── Oda Form Modal (Ekle & Düzenle) ───────────────────────────
function OdaFormModal({ oda, odaTipleri, odaGruplari, onKapat, onKaydet, kaydediliyor, hata }) {
  const yeni = !oda?.odaId;
  const [form, setForm] = useState(oda
    ? { ...oda, arizaliMi: oda.arizaliMi ?? false, arizaNedeni: oda.arizaNedeni ?? '' }
    : { odaNo: '', kat: 1, kisiKapasitesi: 2, durum: 'Temiz', arizaliMi: false, arizaNedeni: '', odaTipiId: '', odaGrubuId: '', aciklama: '', aktifMi: true });
  const [hatalar, setHatalar] = useState({});
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setHatalar(p => ({ ...p, [k]: null })); };

  const handleDurumChange = (v) => {
    if (v === 'Arızalı') {
      setForm(p => ({ ...p, durum: 'Arızalı', arizaliMi: true }));
    } else {
      setForm(p => ({ ...p, durum: v, arizaliMi: false, arizaNedeni: '' }));
    }
    setHatalar(p => ({ ...p, durum: null }));
  };

  const INP = (hata) =>
    `w-full border-2 rounded-2xl px-5 py-3.5 text-sm font-bold text-gray-800 transition-[border-color,box-shadow,background-color] duration-200 bg-white focus:outline-none ${
      hata ? 'border-rose-400 bg-rose-50 focus:border-rose-500 shadow-rose-50 shadow-inner' : 'border-gray-100 focus:border-blue-400 focus:ring-4 focus:ring-blue-50 shadow-sm'
    }`;

  const handleKaydet = () => {
    const h = {};
    if (!form.odaNo) h.odaNo = 'Oda No zorunludur.';
    if (!form.odaTipiId) h.odaTipiId = 'Tip seçiniz.';
    if (Object.keys(h).length > 0) { setHatalar(h); return; }
    onKaydet(form);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-0 lg:p-4 backdrop-blur-md animate-fade-in shadow-2xl leading-none">
      <div className="bg-white w-full max-w-xl h-full sm:h-auto sm:max-h-[95vh] rounded-none lg:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden border border-white/20 animate-scale-in">
        {/* Modal Header */}
        <div className="relative flex items-center justify-between px-8 py-7 bg-gradient-to-r from-blue-600 to-indigo-700 overflow-hidden">
          <div className="absolute top-0 right-0 -mr-12 -mt-12 w-48 h-48 bg-white/10 rounded-full blur-2xl pointer-events-none transform-gpu" style={{ willChange: 'transform' }}></div>
          <div className="relative flex items-center gap-4 text-left leading-none">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-2xl flex items-center justify-center text-2xl shadow-inner border border-white/30">
              {yeni ? '➕' : '✏️'}
            </div>
            <div>
              <h2 className="text-white font-black text-lg tracking-tight uppercase leading-none">{yeni ? 'Yeni Oda Tanımla' : `Oda ${oda.odaNo} Düzenle`}</h2>
              <p className="text-white/70 text-[10px] font-bold uppercase tracking-widest mt-1.5 opacity-80 leading-none">Fiziksel envanter ve kapasite yönetimi</p>
            </div>
          </div>
          <button onClick={onKapat} className="relative z-10 text-white/60 hover:text-white w-10 h-10 flex items-center justify-center rounded-xl hover:bg-white/10 transition-all text-xl leading-none">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6 no-scrollbar leading-none">
          {(hata || hatalar.genel) && <div className="bg-rose-50 border-2 border-rose-100 rounded-2xl px-5 py-4 text-xs text-rose-600 font-black uppercase tracking-widest leading-none animate-shake">⚠️ {hata || hatalar.genel}</div>}

          <div className="grid grid-cols-2 gap-6 leading-none text-left">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">ODA NO *</label>
              <input value={form.odaNo} onChange={e => set('odaNo', e.target.value.toUpperCase())} className={INP(hatalar.odaNo)} placeholder="ÖRN: 101" autoFocus />
              <FormHata mesaj={hatalar.odaNo} />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">KAT NO</label>
              <input type="number" value={form.kat} onChange={e => set('kat', e.target.value)} className={INP(false)} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6 leading-none text-left">
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">KİŞİ KAPASİTESİ</label>
              <input type="number" min="1" value={form.kisiKapasitesi} onChange={e => set('kisiKapasitesi', e.target.value)} className={INP(false)} />
            </div>
            <div>
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">ANLIK DURUM</label>
              <select value={form.durum} onChange={e => handleDurumChange(e.target.value)} className={INP(false) + ' cursor-pointer'}>
                {DURUMLAR.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
          </div>

          {form.durum === 'Arızalı' && (
            <div className="leading-none text-left">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">ARIZA NEDENİ</label>
              <input value={form.arizaNedeni || ''} onChange={e => set('arizaNedeni', e.target.value)}
                className={INP(false)} placeholder="Kısa arıza açıklaması..." />
            </div>
          )}

          <div className="leading-none text-left text-left">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">ODA TİPİ *</label>
            <select value={form.odaTipiId} onChange={e => set('odaTipiId', e.target.value ? parseInt(e.target.value) : '')} className={INP(hatalar.odaTipiId) + ' cursor-pointer'}>
              <option value="">Seçiniz</option>
              {odaTipleri
                .filter(t => t.aktifMi || t.odaTipiId === parseInt(form.odaTipiId))
                .map(t => <option key={t.odaTipiId} value={t.odaTipiId}>{t.ad}{!t.aktifMi ? ' (Pasif)' : ''}</option>)}
            </select>
            <FormHata mesaj={hatalar.odaTipiId} />
          </div>

          <div className="leading-none text-left text-left">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">KAT / GRUP</label>
            <select value={form.odaGrubuId || ''} onChange={e => set('odaGrubuId', e.target.value ? parseInt(e.target.value) : null)} className={INP(false) + ' cursor-pointer'}>
              <option value="">Grup Yok</option>
              {odaGruplari.map(g => <option key={g.odaGrubuId} value={g.odaGrubuId}>{g.grupAdi}</option>)}
            </select>
          </div>

          <div className="leading-none text-left">
            <label className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] block mb-3 ml-1">DAHİLİ NOTLAR</label>
            <textarea rows={2} value={form.aciklama || ''} onChange={e => set('aciklama', e.target.value)}
              className={INP(false) + ' resize-none'} placeholder="Oda ile ilgili operasyonel notlar..." />
          </div>

          <div className="pt-2">
             <label className="flex items-center justify-between bg-gray-50/50 p-4 rounded-2xl border-2 border-gray-100 shadow-inner group select-none cursor-pointer">
                <span className={`text-[10px] font-black uppercase tracking-widest transition-colors duration-300 ${form.aktifMi ? 'text-blue-600' : 'text-gray-400'}`}>ODA REZERVASYONA AÇIK (AKTİF)</span>
                <div className="relative leading-none">
                  <input type="checkbox" checked={form.aktifMi} onChange={e => set('aktifMi', e.target.checked)} className="sr-only peer" />
                  <div className="w-12 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-6 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 shadow-sm transform-gpu"></div>
                </div>
             </label>
          </div>
        </div>

        <div className="px-8 py-6 flex gap-4 justify-end border-t border-gray-50 bg-gray-50/50">
          <button onClick={onKapat} className="px-8 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-black text-gray-400 hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
          <button onClick={handleKaydet} disabled={kaydediliyor}
            className="px-10 py-3.5 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 disabled:opacity-60 text-white rounded-2xl text-xs font-black shadow-xl shadow-blue-200 transition-all hover:-translate-y-1 active:translate-y-0 tracking-widest uppercase">
            {kaydediliyor ? '⌛ KAYDEDİLİYOR...' : '💾 ODAYI KAYDET'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OdaTanimlari() {
  const [odalar, setOdalar] = useState([]);
  const [odaTipleri, setOdaTipleri] = useState([]);
  const [odaGruplari, setOdaGruplari] = useState([]);
  const [cinOdaIdler, setCinOdaIdler] = useState(new Set());
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState(null);
  const [view, setView] = useState('liste');
  const [secili, setSecili] = useState(null);
  const [silOnay, setSilOnay] = useState(null);
  const [siliniyor, setSiliniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [kf, setKfState] = useState({ no: '', grup: '', durum: '', tip: '' });

  const fetchAll = useCallback(async () => {
    setYukleniyor(true); setHata(null);
    try {
      const [odaData, tipler, gruplar] = await Promise.all([apiRequest('Oda'), apiRequest('OdaTipi'), apiRequest('OdaGrubu')]);
      setOdalar((odaData || []).sort((a, b) => String(a.odaNo || '').localeCompare(String(b.odaNo || ''), undefined, { numeric: true, sensitivity: 'base' })));
      setOdaTipleri(tipler || []); setOdaGruplari(gruplar || []);
    } catch { setHata('Veriler yüklenemedi.'); }
    try {
      const rezData = await apiRequest('rezervasyonlar');
      setCinOdaIdler(new Set((rezData || []).filter(r => r.durum === 'C-IN Yapıldı').map(r => r.odaId)));
    } catch { /* doluluk sayacı olmadan devam et */ }
    setYukleniyor(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleKaydet = async (form) => {
    setKaydediliyor(true); setHata(null);
    try {
      if (form.odaId) {
        await apiRequest(`Oda/${form.odaId}`, 'PUT', { ...form, odaNo: form.odaNo.toUpperCase() });
      } else {
        await apiRequest('Oda', 'POST', { ...form, odaNo: form.odaNo.toUpperCase() });
      }
      await fetchAll(); setView('liste'); setSecili(null);
    } catch (e) { setHata(e?.message || 'Hata oluştu.'); }
    setKaydediliyor(false);
  };

  const handleSil = async (id) => {
    setSiliniyor(true);
    try { await apiRequest(`Oda/${id}`, 'DELETE'); setOdalar(p => p.filter(o => o.odaId !== id)); setSilOnay(null); }
    catch { alert('Hata oluştu.'); }
    setSiliniyor(false);
  };

  const filtreli = odalar.filter(o =>
    (kf.no === '' || o.odaNo?.toLocaleLowerCase('tr-TR').includes(kf.no.toLocaleLowerCase('tr-TR'))) &&
    (kf.durum === '' || o.durum?.toLocaleLowerCase('tr-TR').includes(kf.durum.toLocaleLowerCase('tr-TR'))) &&
    (kf.tip === '' || o.odaTipiAd?.toLocaleLowerCase('tr-TR').includes(kf.tip.toLocaleLowerCase('tr-TR'))) &&
    (kf.grup === '' || o.odaGrubuAd?.toLocaleLowerCase('tr-TR').includes(kf.grup.toLocaleLowerCase('tr-TR')))
  );

  const ist = {
    toplam: odalar.length,
    bos: odalar.filter(o => o.durum === 'Temiz').length,
    dolu: odalar.filter(o => cinOdaIdler.has(o.odaId)).length,
    kirli: odalar.filter(o => o.durum === 'Kirli').length,
    arizali: odalar.filter(o => o.arizaliMi).length,
  };

  const isOpen = view === 'ekle' || view === 'duzenle';

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn leading-none" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="🛌"
        iconColor="from-blue-500 to-indigo-700"
        iconShadow="shadow-blue-200"
        title="Oda Tanımları"
        subtitle="Envanter yönetimi ve oda durum takibi"
      >
        <button onClick={() => { setSecili(null); setView('ekle'); setHata(null); }}
          className="group flex-shrink-0 flex items-center justify-center gap-3 bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-10 py-4 rounded-2xl text-[10px] font-black shadow-2xl shadow-blue-100 transition-[background-color,transform] duration-300 hover:-translate-y-1 active:translate-y-0 uppercase tracking-widest relative overflow-hidden leading-none">
          <span className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></span>
          <span className="relative text-xl transition-transform group-hover:rotate-90 leading-none">＋</span>
          <span className="relative">YENİ ODA EKLE</span>
        </button>
      </PageHeader>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 leading-none">
        {[
          { label: 'TOPLAM ODA', value: ist.toplam, icon: '🏨', color: 'from-gray-700 to-gray-900 shadow-gray-200' },
          { label: 'BOŞ / HAZIR', value: ist.bos, icon: '✅', color: 'from-emerald-500 to-teal-600 shadow-emerald-100' },
          { label: 'DOLU / OK', value: ist.dolu, icon: '🔵', color: 'from-blue-500 to-indigo-600 shadow-blue-100' },
          { label: 'KİRLİ / STOP', value: ist.kirli, icon: '🔴', color: 'from-rose-500 to-red-600 shadow-rose-100' },
          { label: 'ARIZALI / FIX', value: ist.arizali, icon: '🔧', color: 'from-amber-400 to-orange-500 shadow-amber-100' },
        ].map(k => (
          <div key={k.label} className={`relative overflow-hidden bg-gradient-to-br ${k.color} rounded-[2rem] p-5 text-white shadow-xl hover:shadow-2xl transition-all duration-300 group`}>
             <div className="absolute -right-4 -bottom-4 text-6xl opacity-10 pointer-events-none group-hover:scale-125 transition-transform duration-500 transform-gpu">{k.icon}</div>
             <p className="text-[9px] font-black uppercase tracking-widest opacity-70 mb-1 leading-none text-left">{k.label}</p>
             <p className="text-xl md:text-2xl font-black tracking-tight leading-none text-left">{k.value}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-[3rem] border border-gray-200 shadow-sm overflow-hidden min-h-[500px]">
        <div className="px-6 md:px-8 py-6 border-b border-gray-100 bg-gray-50/30 flex flex-col xl:flex-row items-center gap-6">
          <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 w-full text-left">
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-[10px] font-black">NO</span>
              <input value={kf.no} onChange={e => setKfState(p => ({ ...p, no: e.target.value }))} placeholder="Oda No..." className="w-full pl-12 pr-4 py-3.5 border-2 border-gray-100 rounded-2xl focus:border-blue-400 focus:outline-none bg-white shadow-sm font-bold text-xs" />
            </div>
            <input value={kf.tip} onChange={e => setKfState(p => ({ ...p, tip: e.target.value }))} placeholder="Oda Tipi..." className="w-full px-5 py-3.5 border-2 border-gray-100 rounded-2xl focus:border-blue-400 focus:outline-none bg-white shadow-sm font-bold text-xs" />
            <input value={kf.grup} onChange={e => setKfState(p => ({ ...p, grup: e.target.value }))} placeholder="Kat/Grup..." className="w-full px-5 py-3.5 border-2 border-gray-100 rounded-2xl focus:border-blue-400 focus:outline-none bg-white shadow-sm font-bold text-xs" />
            <select value={kf.durum} onChange={e => setKfState(p => ({ ...p, durum: e.target.value }))} className="w-full px-5 py-3.5 border-2 border-gray-100 rounded-2xl focus:border-blue-400 focus:outline-none bg-white shadow-sm font-black text-[10px] uppercase tracking-widest cursor-pointer">
              <option value="">Tüm Durumlar</option>
              {DURUMLAR.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="flex items-center justify-between xl:justify-end gap-5 w-full xl:w-auto ml-auto leading-none">
             <button onClick={() => setKfState({ no: '', grup: '', durum: '', tip: '' })} className="px-6 py-3.5 text-[10px] font-black text-rose-500 uppercase tracking-widest hover:bg-rose-50 rounded-2xl transition-all whitespace-nowrap active:scale-95 leading-none">SIFIRLA</button>
             <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest whitespace-nowrap">SONUÇ: <b className="text-blue-600 text-xs">{filtreli.length}</b></p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 p-6 md:p-8">
          {yukleniyor ? (
            <div className="col-span-full">
              <LoadingSpinner fullPage text="Veriler Hazırlanıyor..." />
            </div>
          ) : filtreli.length === 0 ? (
            <div className="col-span-full">
              <EmptyState icon="🛌" text="Aranan kriterde bir oda bulunamadı" />
            </div>
          ) : filtreli.map(o => {
            const dur = DURUM_RENKLER[o.durum] || DURUM_RENKLER['Temiz'];
            const pasif = !o.aktifMi || !o.odaTipiAktifMi || !o.grupAktifMi;
            return (
              <div key={o.odaId} className={`group relative bg-white border-2 border-gray-100 rounded-[2.5rem] p-7 transition-all duration-500 shadow-sm hover:shadow-2xl hover:border-blue-100 hover:-translate-y-2 transform-gpu ${pasif ? 'grayscale opacity-60 bg-gray-50/30' : ''}`}>
                <div className="flex items-center justify-between mb-8 text-left leading-none">
                  <div className="flex flex-col">
                    <span className="text-[9px] font-black text-gray-300 uppercase tracking-widest mb-2 opacity-60">ODA NO</span>
                    <span className="text-3xl font-black text-gray-800 group-hover:text-blue-600 transition-colors font-mono tracking-tighter leading-none">{o.odaNo}</span>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-4 py-2 rounded-2xl border text-[10px] font-black uppercase tracking-widest ${dur.bg} ${dur.text} ${dur.border} shadow-sm leading-none flex items-center gap-1.5`}>
                       <span className={`w-1.5 h-1.5 rounded-full ${dur.dot} animate-pulse`}></span>
                       {o.durum}
                    </span>
                    {o.arizaliMi && o.durum !== 'Arızalı' && (
                      <span className="px-3 py-1 rounded-xl bg-amber-50 text-amber-600 border border-amber-100 text-[9px] font-black uppercase tracking-widest leading-none">🔧 Arızalı</span>
                    )}
                    {pasif && <span className="text-[8px] font-black text-rose-500 uppercase tracking-tighter animate-pulse">SATIŞA KAPALI</span>}
                  </div>
                </div>

                <div className="space-y-4 mb-10 text-left leading-none">
                  <div className="flex items-center gap-4 group/item">
                    <div className="w-12 h-12 bg-gray-50 group-hover/item:bg-blue-50 rounded-2xl flex items-center justify-center text-2xl transition-colors duration-300">🛋️</div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest leading-none mb-1.5 opacity-60">Oda Tipi</p>
                      <p className={`text-xs font-black tracking-tight leading-none uppercase truncate ${o.odaTipiAd ? 'text-gray-700' : 'text-rose-500 animate-pulse italic'}`}>
                        {o.odaTipiAd || '⚠️ TİP SEÇİLMELİ'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 group/item">
                    <div className="w-12 h-12 bg-gray-50 group-hover/item:bg-blue-50 rounded-2xl flex items-center justify-center text-2xl transition-colors duration-300">🏢</div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest leading-none mb-1.5 opacity-60">Kat / Grup</p>
                      <p className={`text-xs font-black tracking-tight leading-none uppercase truncate ${o.odaGrubuAd ? 'text-gray-700' : 'text-amber-500 italic'}`}>
                        {o.odaGrubuAd || '⚠️ GRUP YOK'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 group/item">
                    <div className="w-12 h-12 bg-gray-50 group-hover/item:bg-blue-50 rounded-2xl flex items-center justify-center text-2xl transition-colors duration-300">👥</div>
                    <div className="min-w-0">
                      <p className="text-[9px] font-black text-gray-300 uppercase tracking-widest leading-none mb-1.5 opacity-60">Kapasite</p>
                      <p className="text-xs font-black text-gray-700 tracking-tight leading-none uppercase truncate">{o.kisiKapasitesi} KİŞİLİK KONAKLAMA</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 relative z-10 leading-none">
                  <EditButton size="lg" onClick={() => { setSecili(o); setView('duzenle'); setHata(null); }} />
                  <DeleteButton size="lg" onClick={() => setSilOnay(o.odaId)} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <UsageInfo
        color="blue"
        description="Oda Tanımları bölümü, otelinizdeki fiziksel odaları sisteme tanımladığınız, temel durum takibini yaptığınız alandır."
        scenarios={[
          { title: "Statü Takibi", content: "Oda listesini kullanarak anlık 'Temiz/Kirli' veya 'Hazır/Arızalı' operasyonel durumlarınızı filtreleyebilirsiniz." },
          { title: "Kapasite Kontrol", content: "Kişi kapasitesi belirlenmiş her oda, rezervasyon esnasında sadece uygun sayıdaki misafir talebine olanak tanır." }
        ]}
      />

      {isOpen && createPortal(
        <OdaFormModal 
          oda={secili} 
          odaTipleri={odaTipleri} 
          odaGruplari={odaGruplari} 
          onKapat={() => { setView('liste'); setSecili(null); setHata(null); }} 
          onKaydet={handleKaydet}
          kaydediliyor={kaydediliyor}
          hata={hata}
        />,
        document.body
      )}

      {silOnay && createPortal(
        <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center px-4 backdrop-blur-md animate-fade-in shadow-2xl leading-none">
          <div className="bg-white rounded-[2.5rem] shadow-2xl p-10 w-full max-w-sm text-center border border-gray-100">
            <div className="w-24 h-24 bg-rose-50 rounded-[2.5rem] flex items-center justify-center mx-auto mb-6 text-5xl shadow-xl shadow-rose-100/50 animate-bounce-subtle leading-none">🗑️</div>
            <h3 className="font-black text-gray-900 text-xl tracking-tight uppercase leading-none">Odayı Sil</h3>
            <p className="text-[11px] font-black text-gray-500 mt-5 mb-10 leading-relaxed uppercase tracking-tight text-center">
               <b className="text-gray-900 font-black uppercase">"ODA {odalar.find(x => x.odaId === silOnay)?.odaNo}"</b> kalıcı olarak silinecek. Emin misiniz?
            </p>
            <div className="flex gap-4">
              <button onClick={() => setSilOnay(null)} className="flex-1 py-4 bg-gray-50 text-gray-600 rounded-2xl text-[10px] font-black hover:bg-gray-100 transition-all uppercase tracking-widest active:scale-95">İPTAL</button>
              <button onClick={() => handleSil(silOnay)} disabled={siliniyor} className="flex-1 py-4 bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-2xl text-[10px] font-black shadow-xl shadow-rose-200 transition-all uppercase tracking-widest active:scale-95 transform-gpu">
                {siliniyor ? '⌛' : 'EVET, SİL'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes bounceSubtle { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .animate-bounce-subtle { animation: bounceSubtle 2s infinite; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}